import {flatten, get, head, isBoolean, isEmpty, isEqual, keyBy, uniqBy} from "lodash"
import randomColor from "randomcolor"
import {v4 as uuid} from "uuid"
import {useModel} from "hooks"
import {CHECKBOX_TYPES, DATA_TYPES, DEFAULT_DATA_TYPES, getStudentDataValues, INITIAL_TYPES} from "sections/Tasks/data"
import {DefaultDataType, DigitalDocumentFieldSetUp, ParticipantType} from "types/tasks"
import {useDigitalSetup} from "./useDigitalSetup"
import {SIGNATURE_METHOD_OPTIONS} from "./DigitalDocSetUp"

export const useDigitalField = () => {
    const model = useModel()
    const {
        conditionOptions,
        multiFieldValuesOptions,
        labelByType,
        fieldHasMultipleOptions,
        getScaleInfo,
        scalePlaceInformation
    } = useDigitalSetup()

    const initFields = ({task, definedUserTypes, pageRef, margin}) => {
        const newFieldValues = []
        const signers = task.digitalDocument.signers
        const {scaleHeight, scaleWidth, pageHeight} = getScaleInfo({pageRef, taskData: task})
        const definedUserTypesKeyById = keyBy(definedUserTypes, "id")
        const dataTypesKeyById = keyBy(DATA_TYPES, "id")
        const defaultDataTypesKeyById = keyBy(DEFAULT_DATA_TYPES, "id")
        const studentDataValues = getStudentDataValues(model)
        const studentData = flatten(studentDataValues.map((item) => item.options))
        const studentDataValuesKeyById = keyBy(studentData, "id")
        const checkboxTypesKeyById = keyBy(CHECKBOX_TYPES, "id")
        const initialTypesKeyById = keyBy(INITIAL_TYPES, "id")
        const signersKeyById = keyBy(signers, "id")
        const fieldIndexInfo = {
            [DigitalDocumentFieldSetUp.SIGNATURE]: 0,
            [DigitalDocumentFieldSetUp.INITIAL]: 0,
            [DigitalDocumentFieldSetUp.TEXT]: 0,
            [DigitalDocumentFieldSetUp.CHECKBOX]: 0,
            [DigitalDocumentFieldSetUp.SELECT]: 0,
            [DigitalDocumentFieldSetUp.FILE]: 0,
            [DigitalDocumentFieldSetUp.GRADE]: 0
        }
        const newFields = task.digitalDocument.fields.map((field) => {
            const placeInformation = field.placeInformation[0]
            const fieldValues = field.fieldValues || []
            field.signatureTypes = field.signerIds.map((signerId) => {
                const signerData = signersKeyById[signerId]
                return {id: signerData.id, type: signerData.type, label: signerData.label}
            })
            field.required = !!field.required
            field.isConditionalRule = !isEmpty(field.parsedDependencies)
            field.dependencies = parsedDependencyFields({field, task})
            field.uuid = field.uuid || uuid()
            field.fieldIndex = fieldIndexInfo[field.type] + 1
            fieldIndexInfo[field.type] += 1
            if (placeInformation) {
                const {height, left, top, width} = placeInformation
                field.placeInformation = scalePlaceInformation(
                    {height, left, top, width},
                    {scaleHeight, scaleWidth},
                    false
                )
                field.placeInformation.top = field.placeInformation.top + (field.page - 1) * (pageHeight + margin)
            }
            switch (field.type) {
                case DigitalDocumentFieldSetUp.SIGNATURE: {
                    initSignature(field, {
                        placeInformation,
                        newFieldValues,
                        scaleHeight,
                        scaleWidth,
                        pageHeight,
                        margin
                    })
                    break
                }
                case DigitalDocumentFieldSetUp.INITIAL: {
                    const fieldValuesWithoutParent = fieldValues.filter((fieldValue) => {
                        const {height, left, top, width} = placeInformation
                        const {
                            height: heightFieldValue,
                            left: leftFieldValue,
                            top: topFieldValue,
                            width: widthFieldValue
                        } = fieldValue.position
                        const parentField = {height, left, top, width}
                        const childField = {
                            height: heightFieldValue,
                            left: leftFieldValue,
                            top: topFieldValue,
                            width: widthFieldValue
                        }
                        return !isEqual(childField, parentField)
                    })
                    if (fieldValuesWithoutParent.length) {
                        const initialFieldValues = fieldValuesWithoutParent.map((fieldValue) => {
                            const {height, left, top, width} = fieldValue.position
                            const fieldValuePlaceInformation = scalePlaceInformation(
                                {height, left, top, width},
                                {scaleHeight, scaleWidth},
                                false
                            )
                            const childFieldPage = fieldValue.page || field.page
                            fieldValuePlaceInformation.top =
                                fieldValuePlaceInformation.top + (childFieldPage - 1) * (pageHeight + margin)
                            return {
                                id: fieldValue.id,
                                type: DigitalDocumentFieldSetUp.INITIAL,
                                parentId: field.id,
                                parentSignatureTypeId: get(field, "signatureTypes[0].id", null),
                                label: fieldValue.label,
                                placeInformation: fieldValuePlaceInformation,
                                signatureTypes: field.signerIds.map((signerId) => {
                                    const signerData = signersKeyById[signerId]
                                    return {id: signerData.id, type: signerData.type, label: signerData.label}
                                })
                            }
                        })
                        newFieldValues.splice(newFieldValues.length, 0, ...initialFieldValues)
                    }
                    field.initialType = initialTypesKeyById[field.initialType]
                    break
                }
                case DigitalDocumentFieldSetUp.TEXT: {
                    field.dataType = dataTypesKeyById[field.dataType]
                    if (field.defaultDataType === DefaultDataType.STUDENT_DATA) {
                        field.defaultData = studentDataValuesKeyById[field.defaultData]
                    }
                    field.defaultDataType = defaultDataTypesKeyById[field.defaultDataType]
                    field.defaultParticipant = signersKeyById[+field.defaultParticipant]
                    break
                }
                case DigitalDocumentFieldSetUp.GRADE: {
                    field.dataType = dataTypesKeyById[field.dataType]
                    break
                }
                case DigitalDocumentFieldSetUp.FILE: {
                    field.fileType = checkboxTypesKeyById[field.fileType]
                    break
                }
                case DigitalDocumentFieldSetUp.CHECKBOX: {
                    const fieldValuesWithoutParent = fieldValues.filter((fieldValue) => {
                        const {height, left, top, width} = placeInformation
                        const {
                            height: heightFieldValue,
                            left: leftFieldValue,
                            top: topFieldValue,
                            width: widthFieldValue
                        } = fieldValue.position
                        const parentField = {height, left, top, width}
                        const childField = {
                            height: heightFieldValue,
                            left: leftFieldValue,
                            top: topFieldValue,
                            width: widthFieldValue
                        }
                        return !isEqual(childField, parentField)
                    })
                    if (fieldValuesWithoutParent.length) {
                        const checkboxFieldValues = fieldValuesWithoutParent.map((fieldValue) => {
                            const {height, left, top, width} = fieldValue.position
                            const fieldValuePlaceInformation = scalePlaceInformation(
                                {height, left, top, width},
                                {scaleHeight, scaleWidth},
                                false
                            )
                            const childFieldPage = fieldValue.page || field.page
                            fieldValuePlaceInformation.top =
                                fieldValuePlaceInformation.top + (childFieldPage - 1) * (pageHeight + margin)
                            return {
                                id: fieldValue.id,
                                type: DigitalDocumentFieldSetUp.CHECKBOX,
                                parentId: field.id,
                                parentSignatureTypeId: get(field, "signatureTypes[0].id", null),
                                label: fieldValue.label,
                                placeInformation: fieldValuePlaceInformation,
                                signatureTypes: field.signerIds.map((signerId) => {
                                    const signerData = signersKeyById[signerId]
                                    return {id: signerData.id, type: signerData.type, label: signerData.label}
                                })
                            }
                        })
                        newFieldValues.splice(newFieldValues.length, 0, ...checkboxFieldValues)
                    }
                    field.checkboxType = checkboxTypesKeyById[field.checkboxType]
                    break
                }
                default:
                    break
            }
            return field
        })
        const allFields = [...newFields, ...newFieldValues]
        const newParticipants = task.digitalDocument.signers.map(({id, type, label, participantType}) => ({
            id,
            type,
            label,
            participantType,
            color: randomColor({luminosity: "bright", format: "rgba", alpha: 0.5})
        }))
        const newDefinedUsers = newParticipants.filter(
            (participant) => participant.participantType !== ParticipantType.UserDataOnly
        )
        let newDefinedUserTypes = task.digitalDocument.signers
            .map((signer) => signer.type)
            .map((signerType) => definedUserTypesKeyById[signerType])
        newDefinedUserTypes = uniqBy(newDefinedUserTypes, "id")
        const scaleInfo = getScaleInfo({pageRef, taskData: task})

        return {allFields, newDefinedUsers, newParticipants, newDefinedUserTypes, scaleInfo}
    }

    const parsedDependencyFields = ({field, task}) => {
        return field.parsedDependencies.map((fieldDependency) => {
            const fieldDependencyData = task.digitalDocument.fields.find((item) => item.uuid === fieldDependency.uuid)
            let condition = null
            let dependencyFieldValue = null
            if (fieldHasMultipleOptions.includes(fieldDependencyData.type)) {
                const [filledOutOption, equalsOption] = multiFieldValuesOptions
                condition = isBoolean(fieldDependency.value) ? filledOutOption : equalsOption
                dependencyFieldValue = fieldDependencyData.fieldValues.find(
                    (fieldValue) => fieldValue.id === fieldDependency.value
                )
            } else {
                condition = head(conditionOptions)
            }
            return {
                uuid: fieldDependencyData
                    ? {
                          uuid: fieldDependencyData.uuid,
                          name: fieldDependencyData.label || labelByType[fieldDependencyData.type]
                      }
                    : null,
                condition,
                value: dependencyFieldValue
            }
        })
    }

    const initSignature = (field, {placeInformation, newFieldValues, scaleHeight, scaleWidth, pageHeight, margin}) => {
        if (placeInformation && placeInformation["dateField"]) {
            field.isAddDateTime = true
            const dateFieldPlaceInfo = scalePlaceInformation(
                placeInformation["dateField"],
                {scaleHeight, scaleWidth},
                false
            )
            dateFieldPlaceInfo.top = dateFieldPlaceInfo.top + (field.page - 1) * (pageHeight + margin)
            newFieldValues.push({
                id: uuid(),
                type: DigitalDocumentFieldSetUp.DATE_FIELD,
                parentId: field.id,
                parentSignatureTypeId: get(field, "signatureTypes[0].id", null),
                placeInformation: dateFieldPlaceInfo
            })
        }
        if (field.signatureMethod) {
            const methodById = keyBy(SIGNATURE_METHOD_OPTIONS, "id")
            field.signatureMethod = methodById[field.signatureMethod]
        }
    }

    const getCircularDependencyFields = (fields) => {
        const visited = new Set<string>()
        const stack = new Set<string>()
        const dependentFields = fields.filter((f) => Array.isArray(f.dependencies) && f.dependencies.length > 0)

        const hasCycle = (field) => {
            if (visited.has(field.uuid)) {
                return false
            }
            visited.add(field.uuid)
            stack.add(field.uuid)
            for (const dependency of field?.dependencies || []) {
                const dependencyField = fields.find((f) => f.uuid === dependency?.uuid?.uuid)
                if (stack.has(dependencyField.uuid) || hasCycle(dependencyField)) {
                    return true
                }
            }
            stack.delete(field.uuid)
            return false
        }
        return dependentFields.filter((field) => hasCycle(field))
    }

    return {initFields, getCircularDependencyFields}
}
